<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form
      v-if="render"
      :aria-busy="saving ? 'true' : 'false'"
    >
      <p
        v-if="saveNotice"
        role="status"
        aria-live="polite"
        class="video-save-feedback"
      >
        {{ saveNotice }}
      </p>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="video-title"
          >
            <b-form-input
              id="video-title"
              v-model="form.title"
              :state="validateState('title')"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="video-slug"
          >
            <b-form-input
              id="video-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="video-lang"
          >
            <language-selector
              v-model="form.lang"
              :current-language="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.status')"
            label-for="video-status"
            :state="validateState('status')"
          >
            <b-form-select v-model="form.status">
              <b-form-select-option
                v-for="(status, index) in statusOptions"
                :key="index"
                :value="status"
              >
                {{ $t(`articles.${status}`) }}
              </b-form-select-option>
            </b-form-select>
            <b-form-invalid
              id="input-status-feedback"
              :label="$t('general.status')"
              :validator="$v.form.status"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="video-category"
            :state="validateState('category')"
          >
            <v-select-custom
              id="video-category"
              v-model="form.category"
              :options="rootCategoryOptions"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.tags')"
            label-for="video-tag"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="video-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.approver')"
            label-for="video-approver"
          >
            <v-select
              id="article-approver"
              v-model="form.approver"
              dir="rtl"
              label="firstName"
              :options="approverOptions"
              @search="fetchApprovers(form.lang, $event)"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="video-editor"
          >
            <v-select-custom
              id="video-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.metaTitle')"
            label-for="article-category-metaTitle"
          >
            <b-form-input
              id="article-category-metaTitle"
              v-model="form.metaTitle"
              :state="validateState('metaTitle')"
            />
            <b-form-invalid
              id="input-metaTitle-feedback"
              :label="$t('general.metaTitle')"
              :validator="$v.form.metaTitle"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('articles.relatedArticles')"
            label-for="video-related-articles"
            :state="validateState('relatedArticles')"
          >
            <v-select
              id="video-related-articles"
              v-model="form.relatedArticles"
              dir="rtl"
              multiple
              label="title"
              :options="articleOptions"
              @search="fetchArticles(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
            <b-form-invalid
              id="input-relatedArticles-feedback"
              :label="$t('articles.relatedArticles')"
              :validator="$v.form.relatedArticles"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.embedVideo')"
            label-for="video-embedVideo"
          >
            <b-form-textarea
              id="video-embedVideo"
              v-model="form.embedVideo"
              rows="5"
            />
            <b-form-invalid
              id="input-embedVideo-feedback"
              :label="$t('general.embedVideo')"
              :validator="$v.form.embedVideo"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="video-keywords"
          >
            <b-form-input
              id="video-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.canonicalUrl')"
            label-for="video-canonicalUrl"
            class="mb-2"
          >
            <b-form-input
              id="video-canonicalUrl"
              v-model="form.canonicalUrl"
              :state="validateState('canonicalUrl')"
            />
            <b-form-invalid
              id="input-canonicalUrl-validator"
              :label="$t('general.canonicalUrl')"
              :validator="$v.form.canonicalUrl"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="video-description"
          >
            <b-form-textarea
              id="video-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <CampaignSettings
            :value="form.campaignSettings || {}"
            :lang="form.lang || 'fa'"
            end-only
            @input="$set(form, 'campaignSettings', $event)"
          />
          <ArticleDoctorEditor
            :mode="form.doctorBlockMode || 'auto'"
            :ids="form.recommendedDoctorIds || []"
            :enabled="form.doctorBlockEnabled === true"
            placement="end"
            :end-only="true"
            :tags="form.tags || []"
            :lang="form.lang || 'fa'"
            @change="setDoctorBlock"
          />
          <b-form-group
            label-for="video-content"
            class="mb-2"
          >
            <CkeditorBasic
              id="video-content"
              v-model="form.content"
              :label="$t('general.content')"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleThumbnail"
            v-model="form.articleThumbnail"
            :label="$t('general.thumbnail') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleThumbnail')"
            :alt.sync="form.articleThumbnailAlt"
            :alt-validator="$v.form.articleThumbnailAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-video-thumbnail-feedback"
                :label="$t('general.thumbnail')"
                :validator="$v.form.articleThumbnail"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="articleCoverImage"
            v-model="form.articleCoverImage"
            :label="$t('general.cover') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('articleCoverImage')"
            :alt.sync="form.articleCoverImageAlt"
            :alt-validator="$v.form.articleCoverImageAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-video-cover-feedback"
                :label="$t('general.cover')"
                :validator="$v.form.articleCoverImage"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col cols="6">
          <b-file-input
            id="articleVideo"
            v-model="form.articleVideo"
            :label="$t('general.video') + ' ' + $t('general.acceptVideo')"
            :placeholder="$t('general.select')"
            :state="validateState('articleVideo')"
            :accept="'.mp4,.mov,.flv,.mkv'"
            :alt.sync="form.articleVideoAlt"
            :alt-validator="$v.form.articleVideoAlt"
            type="video"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-video-article-video-feedback"
                :label="$t('general.video')"
                :validator="$v.form.articleVideo"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="saving || saveUnknown"
            @click="submitForm"
          >
            {{ saving ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormSelect, BFormSelectOption, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import VSelectOption from '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import vSelect from 'vue-select'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import store from '@/store'
import useVideosList from './useVideosList'
import {
  restoreVideoReferences, videoReferencePatch, videoUploadFields, videoEditMessages, videoSaveError, boundedVideoSave, snapshotVideoForm,
} from './video-edit-payload'
import CampaignSettings from '../contentCampaign/CampaignSettings.vue'
import ArticleDoctorEditor from '../article/components/ArticleDoctorEditor.vue'

export default {
  name: 'VideoEdit',
  beforeRouteLeave(to, from, next) { next(!this.saving || (this.saveConfirmed && to.name === 'pages-videos-list')) },
  components: {
    ArticleDoctorEditor,
    CampaignSettings,
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    VSelectOption,
    languageSelector,
    vSelect,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        metaTitle: {
          required,
        },
        slug: {
          required,
          isUnique: () => this.isUniqueSlug,
        },
        tags: {},
        category: {
          required,
        },
        keywords: {
          minLength: minLength(3),
        },
        embedVideo: {
          required: requiredIf(function () {
            return !this.form.articleVideo
          }),
        },
        description: {
          minLength: minLength(10),
        },
        articleThumbnail: {
          required,
        },
        articleCoverImage: {
          required,
        },
        articleVideo: {
          required: requiredIf(function () {
            return !this.form.embedVideo
          }),
        },
        articleVideoAlt: {
          required: requiredIf(function () {
            return !this.form.embedVideo
          }),
        },
        articleThumbnailAlt: {
          required,
          minLength: minLength(3),
        },
        articleCoverImageAlt: {
          required,
          minLength: minLength(3),
        },
        relatedArticles: {},
        canonicalUrl: {},
        status: {
          required,
        },
      },
    }
  },
  setup() {
    const {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      rootCategoryOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchCategories,
      fetchItem,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      statusOptions,
      tags,
      updateItem,
      userOptions,
    } = useVideosList()
    return {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      rootCategoryOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchCategories,
      fetchItem,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      statusOptions,
      tags,
      updateItem,
      userOptions,
    }
  },
  data() {
    return {
      editorOptions: [],
      saving: false,
      saveConfirmed: false,
      saveDisposed: false,
      saveUnknown: false,
      saveNotice: '',
    }
  },
  computed: {
    saveWords() { return videoEditMessages[this.$i18n.locale] || videoEditMessages.fa },
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  watch: {
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.fetchItem(this.itemId).then(() => {
      this.form = restoreVideoReferences(this.form, this.item || {})
      this.fetchTags(this.form.lang)
      this.fetchApprovers()
      this.fetchUsersByRole({
        role: 'editor',
      }).then(res => {
        this.editorOptions = res
      })
      this.fetchCategories(this.form.lang, '', '', 'null')
      this.fetchCategories(this.form.lang)
      this.fetchArticles(this.form.lang)
    })
  },
  beforeDestroy() { this.saveDisposed = true },
  methods: {
    setDoctorBlock(value) {
      Object.keys(value).forEach(key => this.$set(this.form, key, value[key]))
    },
    changeSelectedLanguage(selectedLang) {
      this.form = restoreVideoReferences(this.convertResource(this.item, selectedLang), this.item)
      this.fetchCategories(selectedLang, '', '', 'null')
      this.fetchArticles(selectedLang)
      this.fetchTags(this.form.lang)
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    async submitForm() {
      if (this.saving || this.saveUnknown) return
      this.saveNotice = ''
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.saveNotice = this.saveWords.invalid
        this.$nextTick(() => {
          const invalid = this.$el.querySelector('.is-invalid')
          if (invalid) { invalid.scrollIntoView({ behavior: 'smooth', block: 'center' }); invalid.focus() }
        })
        return
      }
      this.saving = true
      this.saveConfirmed = false
      this.saveNotice = this.saveWords.pending
      const submission = snapshotVideoForm(this.form)
      const baseline = { newTagIds: snapshotVideoForm(this.newTagIds), json: JSON.stringify(this.form), media: [this.form.articleVideo, this.form.articleCoverImage, this.form.articleThumbnail] }
      try {
        const previous = this.convertResource(this.item, submission.lang)
        const uploadList = videoUploadFields(submission, previous)
        const uploaded = uploadList.length ? await boundedVideoSave(this.handleUpload(submission, uploadList)) : submission
        await this.sendData(uploaded, baseline)
      } catch (_) { this.saveNotice = this.saveWords.failed } finally { this.saving = false }
    },
    async sendData(form, baseline) {
      const data = {
        ...videoReferencePatch(form, this.item || {}),
        title: form.title,
        metaTitle: form.metaTitle,
        slug: form.slug,
        keywords: form.keywords,
        description: form.description,
        tagIds: this.createTagIdArray(form.tags, baseline ? baseline.newTagIds : this.newTagIds),
        relatedArticleIds: form.relatedArticles ? form.relatedArticles.map(item => item._id) : [],
        status: form.status,
        embedVideo: form.embedVideo,
        content: form.content,
        ...(form.campaignSettings ? { campaignSettings: form.campaignSettings } : {}),
        recommendedDoctorIds: form.recommendedDoctorIds || [],
        doctorBlockMode: form.doctorBlockMode || 'auto',
        doctorBlockEnabled: form.doctorBlockMode === 'manual',
        doctorBlockPlacement: 'end',
        canonicalUrl: form.canonicalUrl,
        type: 'video',
      }
      const submittedForm = baseline ? baseline.json : JSON.stringify(this.form)
      const submittedMedia = baseline ? baseline.media : [this.form.articleVideo, this.form.articleCoverImage, this.form.articleThumbnail]
      try {
        await boundedVideoSave(store.dispatch('crud/update', { data: { id: this.itemId, data, lang: form.lang }, endpoint: 'articles' }))
        if (this.saveDisposed) return
        if (JSON.stringify(this.form) !== submittedForm || submittedMedia.some((value, index) => value !== [this.form.articleVideo, this.form.articleCoverImage, this.form.articleThumbnail][index])) {
          this.saveNotice = this.saveWords.savedWithNewChanges
          return
        }
        this.saveNotice = this.saveWords.saved
        this.saveConfirmed = true
        this.$router.push({ name: 'pages-videos-list' })
      } catch (error) {
        const status = error && error.response && error.response.status
        this.saveUnknown = ![400, 401, 403, 409, 422].includes(status)
        this.saveNotice = videoSaveError(error, this.saveWords)
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
.video-save-feedback { padding: 12px; border-inline-start: 3px solid #16858b; background: #f0f8f8; overflow-wrap: anywhere; }
</style>
